import { sumBy, uniq } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Alert,
    AlertHandle,
    autoinject,
    bind,
    inject,
    Loader,
    Modal,
    ModalHandle,
    Optional,
    Nullable,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledButtonDropdown } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { FavoriteButton } from '../Core/FavoriteButton';
import { ActiveDisplay } from './ActiveDisplay';
import { DisplayEventGroup, DisplayEventsList } from './DisplayEventsList';
import { DisplaysStore } from './DisplaysStore';
import { BsCalendar } from '@react-icons/all-files/bs/BsCalendar';
import LinkContainer from 'react-router-bootstrap/lib/LinkContainer';
import {TicketModal} from "../Core/TicketModal";

export interface IDisplayEventsModalProps {
    readonly display: ActiveDisplay;
    readonly modalHandle: ModalHandle;
    readonly displayFilter?: Mri.DisplayFilters.DisplayFilter;
    readonly showOnlyAlerts?: boolean;
    readonly hideAlerts?: boolean;
    readonly history: any;
}

@autoinject
@observer
export class DisplayEventsModal extends React.Component<IDisplayEventsModalProps> {
    public render() {
        let eventType = this.props.showOnlyAlerts ? "Alert" : "Event";

        return (
            <Modal
                title={this.title}
                modalHandle={this.props.modalHandle}
                footerClass="d-none"
                size="md"
                className="my-2"
                bodyClass="vh-50"
                onOpened={this.loadAsync}
                onClosed={this.clearInfo}
            >
                <Alert alertHandle={this.alertHandle} />
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12 pe-0">
                            <div className="d-flex">
                                <div className="d-inline-block">
                                    <button className="btn btn-primary mb-2" onClick={this.createTicket}>
                                        Create Ticket For Selected
                                    </button>

                                    <div>
                                        <input type="checkbox" checked={this.selectAll} onChange={this.checkSelectAll} />
                                        <label className="mx-1">Select All</label>
                                    </div>
                                </div>
                                <div className="d-inline-block ms-auto">
                                    <div>
                                        <input type="checkbox" checked={this.hideInactive} onChange={this.toggleInactive} />
                                        <label className="mx-1">Hide Inactive</label>
                                    </div>

                                    <LinkContainer to={`/displays/${this.props.display.serialNumber}/events`}>
                                        <a className="btn btn-link">
                                            View All Events/Alerts
                                        </a>
                                    </LinkContainer>
                                </div>
                            </div>

                            {this.displayEvents && !this.loading && (
                                <DisplayEventsList
                                    displayEvents={this.displayEvents}
                                    renderRow={this.renderRow}
                                    serialNumber={this.props.display.serialNumber}
                                />
                            )}
                            <Loader loading={this.loading} showSpinner />
                        </div>
                    </div>

                    <Modal
                        className="modal-sm"
                        title="Confirm Action"
                        modalHandle={this.modalHandle}
                        btnClass="w-50"
                        footerClass="d-flex justify-content-center"
                    >
                        <Loader loading={this.loadingInfo} showSpinner message="Loading Effect..." />
                        {!this.loadingInfo && !!this.info && (
                            <div className="text-center">
                                Affects <strong>{this.info.numDisplays}</strong> displays and{' '}
                                <strong>{this.info.numEvents}</strong> {eventType}s
                            </div>
                        )}
                        {!this.loadingInfo && !this.info && (
                            <div className="text-center">{`${
                                this.numCheckedDisplayEvents || this.displayEvents?.length || 0
                            } ${eventType}s will be affected`}</div>
                        )}
                        {!this.loadingInfo && !!this.infoError && (
                            <div className="text-center">
                                <strong>An error occurred. But you can still continue if you want.</strong>
                                <small>
                                    {typeof this.infoError == 'string'
                                        ? this.infoError
                                        : typeof this.infoError == 'object' && typeof this.infoError.message == 'string'
                                            ? this.infoError.message
                                            : 'An error has occured. Please try again.'}
                                </small>
                            </div>
                        )}
                    </Modal>
                </div>
                
                {this.props.display && (
                    <TicketModal
                        modalHandle={this.createTicketModalHandle}
                        serialNumber={this.props.display.serialNumber}
                        displayEvents={this.selectedDisplayEvents}
                        display={this.props.display}
                    />
                )}
            </Modal>
        );
    }

    @bind
    private renderRow(displayEvent: Mri.DisplayEvents.DisplayEvent, group: DisplayEventGroup, isHead: boolean) {
        return (
            <div className="d-inline-block pe-3">
                <div className="d-flex align-items-center h-100">
                    <input
                        type="checkbox"
                        checked={ this.checkedDisplayEvents.get(displayEvent.displayEventGuid) ?? false }
                        onChange={e => this.onChange(e, displayEvent, group, isHead)}
                    />
                </div>
            </div>
        );
    }
    
    @bind
    @action
    private clearInfo(){
        this.info = undefined;
    }

    @bind
    @action
    private async loadAsync() {
        this.loading = true;
        try {
            await this.displaysStore.loadCurrentDisplayEventsAsync(this.props.display.serialNumber);
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    private checkDisplayEvent(checked: boolean, displayEvent: Mri.DisplayEvents.DisplayEvent) {
        if (checked && !this.checkedDisplayEvents.get(displayEvent.displayEventGuid))
            this.checkedDisplayEvents.set(displayEvent.displayEventGuid, true);
        else if (!checked) this.checkedDisplayEvents.delete(displayEvent.displayEventGuid);
    }

    @bind
    @action
    private checkSelectAll() {
        this.selectAll = !this.selectAll;
        if (this.displayEvents) {
            for (const de of this.displayEvents) {
                this.checkDisplayEvent(this.selectAll, de);
            }
        }
    }

    @bind
    @action
    private toggleInactive() {
        this.hideInactive = !this.hideInactive;
    }

    @bind
    @action
    private onChange(
        event: React.FormEvent<HTMLInputElement>,
        displayEvent: Mri.DisplayEvents.DisplayEvent,
        group: DisplayEventGroup,
        isHead: boolean
    ) {
        const target = event.target as HTMLInputElement;

        if (isHead && !group.open) {
            if (group.displayEvents) {
                for (const de of group.displayEvents) {
                    this.checkDisplayEvent(target.checked, de);
                }
            }
        }

        this.checkDisplayEvent(target.checked, displayEvent);
    }

    @bind
    @action
    private createTicket() {
        this.createTicketModalHandle.open();
    }

    @computed
    private get selectedDisplayEvents() {
        return Array.from(
            this.checkedDisplayEvents,
            ([guid, v]) => this.displayEvents!.find(de => de.displayEventGuid == guid)!
        );
    }

    @computed
    private get displayEvents() {
        var events = this.displaysStore.currentEvents.get(this.props.display.serialNumber);
        if (events && this.props.hideAlerts) {
            events = events.filter(e => e.displayEventStatus != Mri.DisplayEvents.DisplayEventStatus.Alert);
        }

        if (events && this.props.showOnlyAlerts) {
            events = events.filter(e => e.displayEventStatus == Mri.DisplayEvents.DisplayEventStatus.Alert);
        }

        if (events && !this.showIgnored) {
            events = events.filter(e => e.incidentLevel != Mri.DisplayApi.IncidentLevel.IncidentIgnored);
            events = events.filter(e => e.displayEventStatus != Mri.DisplayEvents.DisplayEventStatus.Ignored);
        }

        if (events && this.hideInactive) {
            events = events.filter(e => e.downtimeActiveIncident != false);
        }
        return events;
    }

    @computed
    private get numCheckedDisplayEvents() {
        const displayEvents = Array.from(
            this.checkedDisplayEvents,
            ([guid, v]) => this.displayEvents!.find(de => de.displayEventGuid == guid)!
        );

        return sumBy(displayEvents, de => de.count || 1);
    }

    private getSelectedMinMaxDuration() {
        let max = 0;
        let min = Number.MAX_VALUE;

        const displayEvents = Array.from(this.checkedDisplayEvents, ([guid, v]) =>
            this.displayEvents!.find(de => de.displayEventGuid == guid)
        );

        for (const de of displayEvents) {
            if (de && de?.downtimeIncidentDuration != null) {
                if (de.downtimeIncidentDuration < min) {
                    min = de.downtimeIncidentDuration;
                }

                if (de.downtimeIncidentDuration > max) {
                    max = de.downtimeIncidentDuration;
                }
            }
        }

        min = Math.floor(min - min * 0.1);
        max = Math.floor(max + max * 0.1);

        return {
            min,
            max,
        };
    }

    private getSelectedTypes() {
        const displayEvents = Array.from(this.checkedDisplayEvents, ([guid, v]) =>
            this.displayEvents!.find(de => de.displayEventGuid == guid)
        );
        const types = displayEvents.filter(de => de?.downtimeIncidentType).map(de => de?.downtimeIncidentType!);

        return uniq(types);
    }

    @computed
    private get title() {
        return (
            <div className="w-100 text-center">
                <FavoriteButton className="align-self-center float-start ms-3" display={this.props.display} />

                <span className="fw-bold">
                    <BsCalendar /> Manage {this.props.showOnlyAlerts ? "Alerts: " : "Events: "} {this.props.display.serialNumber}
                </span>
            </div>
        );
    }

    @observable
    private modalHandle = new ModalHandle();
    @observable
    private alertHandle = new AlertHandle();
    @observable
    private loading = false;
    @observable
    private loadingInfo = false;
    @observable
    private infoError: any | undefined;
    @observable
    private selectAll = false;
    @observable
    private showIgnored = false;
    @observable
    private hideInactive= false;
    @observable
    private info: Mri.DisplayEventProfiles.DisplayEventProfileInfo | undefined;
    @observable
    private checkedDisplayEvents = new Map<string, boolean>();
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    private createTicketModalHandle = new ModalHandle();
}
